import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface HKDTooltipBubbleProps {
  isVisible: boolean;
  onClose: () => void;
}

export const HKDTooltipBubble: React.FC<HKDTooltipBubbleProps> = ({ isVisible, onClose }) => {
  const { i18n } = useTranslation();
  const [animate, setAnimate] = useState(false);
  const [position, setPosition] = useState({ top: 88, right: 180, pointerRight: 48, buttonCenterX: 0 });

  useEffect(() => {
    if (isVisible) {
      // Calculer la position dynamiquement par rapport au CurrencySelector et à la navbar
      const updatePosition = () => {
        const currencyButton = document.getElementById('currency-selector-button');
        const header = document.querySelector('header');
        
        if (currencyButton && header) {
          const buttonRect = currencyButton.getBoundingClientRect();
          const headerRect = header.getBoundingClientRect();
          const windowWidth = window.innerWidth;
          
          // Position de la bulle : positionnée par rapport à la navbar, pas au bouton
          // La bulle doit être juste sous la navbar, la pointe pointe vers le bouton
          const navbarBottom = headerRect.bottom; // Bordure inférieure de la navbar
          const bubbleTop = navbarBottom + 10; // 10px sous la navbar pour que la bulle soit bien visible (pas enfoncée)
          const bubbleWidth = 180; // min-w-[180px]
          
          // Centre du bouton par rapport au bord droit de la fenêtre
          const buttonCenterX = windowWidth - (buttonRect.left + buttonRect.width / 2);
          
          // Position de la bulle : centrée sous le bouton
          const bubbleRight = buttonCenterX - (bubbleWidth / 2);
          
          // Position de la pointe : doit pointer vers le centre du bouton
          // La pointe est au centre de la bulle, mais elle pointe vers le bouton
          const pointerOffset = bubbleWidth / 2;
          
          setPosition({
            top: bubbleTop,
            right: Math.max(16, bubbleRight), // Minimum 16px du bord
            pointerRight: pointerOffset,
            // Ajuster la position de la pointe pour qu'elle pointe vers le bouton
            buttonCenterX: buttonCenterX
          });
        }
      };

      // Calculer la position au montage et lors du redimensionnement
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      // Animation d'entrée
      setTimeout(() => setAnimate(true), 50);

      // Auto-fermeture après 5 secondes
      const timer = setTimeout(() => {
        setAnimate(false);
        setTimeout(onClose, 300);
      }, 5000);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    } else {
      setAnimate(false);
    }
  }, [isVisible, onClose, i18n.language]);

  if (!isVisible) return null;

  const isRTL = i18n.dir(i18n.language) === 'rtl';

  return (
    <div
      className={`fixed z-[60] transition-all duration-500 ${
        animate ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
      }`}
      style={{
        top: `${position.top}px`,
        ...(isRTL 
          ? { left: `${position.right}px` }
          : { right: `${position.right}px` }
        ),
        transformOrigin: isRTL ? 'top left' : 'top right'
      }}
    >
      {/* Bulle de dialogue style BD */}
      <div className="relative">
        {/* Pointe de la bulle vers le HAUT (vers le Currency Selector) - touche la bordure */}
        <div 
          className="absolute"
          style={{
            top: '-12px', // Hauteur de la pointe qui touche exactement rect.bottom
            ...(isRTL
              ? { left: `${position.pointerRight}px`, transform: 'translateX(-50%)' }
              : { right: `${position.pointerRight}px`, transform: 'translateX(50%)' }
            )
          }}
        >
          <div className="relative">
            {/* Bordure du triangle pointant vers le haut */}
            <div className="w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-b-[16px] border-b-gray-900 dark:border-b-white"></div>
            {/* Remplissage du triangle */}
            <div className="absolute top-[3px] left-[2px] w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[14px] border-b-white dark:border-b-gray-800"></div>
          </div>
        </div>

        {/* Contenu de la bulle */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-[3px] border-gray-900 dark:border-white px-5 py-3 min-w-[200px]">
          <p className="text-sm font-bold text-gray-900 dark:text-white text-center">
            {i18n.language === 'zh' || i18n.language.startsWith('zh-') 
              ? '香港元 (HKD) 可用' 
              : 'Hong Kong Dollar (HKD) available'}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 text-center mt-1">
            {i18n.language === 'zh' || i18n.language.startsWith('zh-')
              ? 'Komby 总部位于香港'
              : 'Komby HQ is in Hong Kong'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HKDTooltipBubble;

